import { zodResolver } from '@hookform/resolvers/zod'
import {
  Alert,
  Button,
  Checkbox,
  DatePicker,
  Drawer,
  Input,
  Select,
  Switch,
  useDrawer,
} from '@iveco-ti/iveco-ds'
import { FormProvider, useForm } from '@iveco-ti/iveco-ds/hook-form'
import { Info, Mail } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { usePermissions } from '@/common/hooks/usePermissions'
import { Gender } from '@/common/models/person'
import { UserModel, UserType } from '@/common/models/user'
import { getLocale } from '@/common/utils/getLocale'
import { translateGender } from '@/common/utils/translateGender'
import { translateUserType } from '@/common/utils/translateUserType'
import { useNewUser } from '../hooks/useNewUser'
import { NewUserForm, NewUserSchema } from '../schemas/NewUserSchema'

export function DrawerNewUser() {
  const { t } = useTranslation(['registers', 'common'])
  const { close } = useDrawer('drawer-new-user')
  const hookForm = useForm<NewUserForm>({
    resolver: zodResolver(NewUserSchema()),
  })
  const { can } = usePermissions()
  const { handleCreateUser, isCreatingUser } = useNewUser()

  const handleSubmit = async (data: NewUserForm) => {
    await handleCreateUser(data).then(() => close())
  }

  const handleCancelNewUser = () => {
    hookForm.reset()
    close()
  }

  const USER_TYPE_OPTIONS = Object.values(UserType)
    .map((type) => ({
      label: translateUserType(t, type),
      value: type,
    }))
    .filter((option) =>
      can('create', {
        __typename: 'User',
        userType: option.value,
      } as UserModel),
    )

  const GENDER_OPTIONS = Object.values(Gender).map((gender) => ({
    label: translateGender(t, gender),
    value: gender,
  }))

  const IS_FOREIGN = hookForm.watch('isForeign')

  return (
    <Drawer.Root id="drawer-new-user">
      <Drawer.Header>
        <h1 className="text-lg text-brand-800 font-semibold">
          {t('registers:users.drawer_new_user.header.title')}
        </h1>
      </Drawer.Header>

      <Drawer.Body>
        <h2 className="text-2xl font-semibold text-brand-700">
          {t('registers:users.drawer_new_user.body.title')}
        </h2>

        <FormProvider {...hookForm}>
          <form
            className="space-y-6"
            onSubmit={hookForm.handleSubmit(handleSubmit)}
          >
            <Input.Icon
              icon={<Mail />}
              name="email"
              label={t('registers:users.drawer_new_user.fields.email.label')}
              placeholder={t(
                'registers:users.drawer_new_user.fields.email.placeholder',
              )}
            />

            <Alert.Root>
              <Alert.Icon icon={<Info />} />

              <Alert.Body>
                <Alert.Description>
                  <p>{t('registers:users.drawer_new_user.alert')}</p>
                </Alert.Description>
              </Alert.Body>
            </Alert.Root>

            <Select.Single
              name="userType"
              label={t(
                'registers:users.drawer_new_user.fields.user_type.label',
              )}
              placeholder={t(
                'registers:users.drawer_new_user.fields.user_type.placeholder',
              )}
              options={USER_TYPE_OPTIONS}
            />

            <h2 className="text-2xl font-semibold text-brand-700">
              {t('registers:users.drawer_new_user.body.subtitle')}
            </h2>

            <div className="flex justify-between gap-4">
              <div className="space-y-2 flex-1">
                {!IS_FOREIGN && (
                  <Input.Mask
                    name="documentNumber"
                    mask="###.###.###-##"
                    label={t(
                      'registers:users.drawer_new_user.fields.cpf.label',
                    )}
                    placeholder={t(
                      'registers:users.drawer_new_user.fields.cpf.placeholder',
                    )}
                  />
                )}

                {IS_FOREIGN && (
                  <Input.Base
                    name="documentNumber"
                    label={t(
                      'registers:users.drawer_new_user.fields.document_number.label',
                    )}
                    placeholder={t(
                      'registers:users.drawer_new_user.fields.document_number.placeholder',
                    )}
                  />
                )}

                <Checkbox
                  name="isForeign"
                  text={t(
                    'registers:users.drawer_new_user.fields.is_foreign.label',
                  )}
                />
              </div>

              <div className="flex-1">
                <Input.Base
                  name="displayName"
                  label={t(
                    'registers:users.drawer_new_user.fields.display_name.label',
                  )}
                  placeholder={t(
                    'registers:users.drawer_new_user.fields.display_name.placeholder',
                  )}
                />
              </div>
            </div>

            <Input.Base
              name="name"
              label={t('registers:users.drawer_new_user.fields.name.label')}
              placeholder={t(
                'registers:users.drawer_new_user.fields.name.placeholder',
              )}
            />

            <div className="flex justify-between gap-4">
              <DatePicker.Single
                locale={getLocale()}
                name="birthDate"
                label={t(
                  'registers:users.drawer_new_user.fields.birth_date.label',
                )}
                placeholder={t(
                  'registers:users.drawer_new_user.fields.birth_date.placeholder',
                )}
              />

              <Select.Single
                name="genre"
                label={t('registers:users.drawer_new_user.fields.gender.label')}
                placeholder={t(
                  'registers:users.drawer_new_user.fields.gender.placeholder',
                )}
                options={GENDER_OPTIONS}
              />
            </div>

            <Switch.Root className="flex justify-between">
              <Switch.Label htmlFor="allowedPosts">
                {t(
                  'registers:users.drawer_new_user.fields.allowed_posts.label',
                )}
              </Switch.Label>

              <Switch.Trigger name="allowedPosts" />
            </Switch.Root>
          </form>
        </FormProvider>
      </Drawer.Body>

      <Drawer.Footer>
        <div className="flex items-center justify-end gap-4">
          <Button.Root
            variant="secondary"
            color="brand"
            onClick={handleCancelNewUser}
            disabled={isCreatingUser}
          >
            {t('registers:users.drawer_new_user.footer.close_button.label')}
          </Button.Root>

          <Button.Root
            variant="primary"
            color="brand"
            onClick={hookForm.handleSubmit(handleSubmit)}
            type="submit"
            isLoading={isCreatingUser}
          >
            {t('registers:users.drawer_new_user.footer.submit_button.label')}
          </Button.Root>
        </div>
      </Drawer.Footer>
    </Drawer.Root>
  )
}
